import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ElevatorRoundedIcon from '@mui/icons-material/ElevatorRounded';
import FitnessCenterRoundedIcon from '@mui/icons-material/FitnessCenterRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LocalParkingRoundedIcon from '@mui/icons-material/LocalParkingRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SquareFootRoundedIcon from '@mui/icons-material/SquareFootRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import Cropper from 'react-easy-crop';
import { MapContainer, Marker, TileLayer, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import AuthContext from '../context/AuthContext';
import ProfileContext from '../context/ProfileContext';
import {
  addGalleryImage,
  addGymEquipment,
  addGymFeature,
  removeGalleryImage,
  removeGymEquipment,
  removeGymFeature,
  sendEmailVerification,
  updateGymProfile,
  updateProfileImage,
} from '../api';
import getCroppedImg from '../utils/cropImage';
import GymAvailabilityCard from './GymAvailabilityCard';
import OpeningHoursEditor, { normalizeOpeningHours } from './OpeningHoursEditor';

const BRAND = {
  navy: '#010449',
  blue: '#2458A6',
  paleBlue: '#EEF3FF',
  orange: '#F28C28',
  border: '#E4E8F0',
};

const gymBackend = (process.env.REACT_APP_GYM_BACKEND_URL || '').replace(/\/$/, '');
const DEFAULT_GYM_PROFILE = '/images/default-gym-profile.png';
const DEFAULT_GYM_GALLERY = '/images/default-gym-gallery.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DEFAULT_POSITION = [48.8566, 2.3522];

const FEATURE_OPTIONS = [
  'Douches', 'Parking', 'Espace restauration', 'Piscine', 'Massage',
  'Kit d’hygiène', 'Laverie', 'Jacuzzi', 'Bain froid', 'Hammam',
  'Sauna', 'Ascenseur', 'Vestiaires', 'Accès PMR',
];

const EQUIPMENT_OPTIONS = [
  'Machines de musculation', 'Tapis de course et cardio',
  'Zone fonctionnelle', 'Espace CrossFit', 'TRX', 'X-Body',
  'Haltères et poids libres', 'Vélos elliptiques', 'Rameurs',
];

const LEGACY_LABELS = {
  'حمام': 'Douches',
  'پارکینگ': 'Parking',
  'بوفه': 'Espace restauration',
  'استخر': 'Piscine',
  'ماساژ': 'Massage',
  'پک بهداشتی یکبار مصرف': 'Kit d’hygiène',
  'لاندری': 'Laverie',
  'جکوزی': 'Jacuzzi',
  'حوضچه ابسرد': 'Bain froid',
  'سونا بخار': 'Hammam',
  'سونا خشک': 'Sauna',
  'آسانسور': 'Ascenseur',
  'دستگاه های وزنه': 'Machines de musculation',
  'تردمیل و هوازی': 'Tapis de course et cardio',
  'فضای فانکشنال': 'Zone fonctionnelle',
  'فضای کراس فیت': 'Espace CrossFit',
  'وزنه دمبل': 'Haltères et poids libres',
};

const FLOOR_OPTIONS = [
  { value: 'لاستیکی', label: 'Caoutchouc' },
  { value: 'فوم', label: 'Mousse' },
  { value: 'وینیل', label: 'Vinyle' },
  { value: 'پی‌وی‌سی', label: 'PVC' },
  { value: 'چوبی', label: 'Bois' },
  { value: 'لمینت', label: 'Stratifié' },
  { value: 'لینولئوم', label: 'Linoléum' },
  { value: 'چوب پنبه‌ای', label: 'Liège' },
  { value: 'تاتامی', label: 'Tatami' },
  { value: 'اپوکسی', label: 'Époxy' },
  { value: 'پلی‌اورتان', label: 'Polyuréthane' },
  { value: 'انعطاف‌دار', label: 'Sol souple' },
  { value: 'ترکیبی', label: 'Sol mixte' },
  { value: 'چمن مصنوعی', label: 'Gazon synthétique' },
  { value: 'بتن صیقلی', label: 'Béton poli' },
  { value: 'چمن طبیعی', label: 'Gazon naturel' },
];

const translateLegacyLabel = (value) => LEGACY_LABELS[value] || value;

const profileMediaUrl = (path) => {
  if (!path || path === 'null') return DEFAULT_GYM_PROFILE;
  if (/^https?:\/\//i.test(path)) return path;
  return `${gymBackend}/medias/profile/${path}`;
};

const galleryMediaUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${gymBackend}/medias/gallery/${path}`;
};

const numericValue = (value, fallback = '') => (
  value === null || value === undefined ? fallback : value
);

const normalizeStructure = (structure = {}) => {
  const normalized = {};
  ['area', 'height', 'floor', 'parking', 'elevator'].forEach((key) => {
    if (structure[key] !== '' && structure[key] !== null && structure[key] !== undefined) {
      normalized[key] = Number(structure[key]);
    }
  });
  return normalized;
};

const sectionCardSx = {
  borderRadius: 4,
  border: `1px solid ${BRAND.border}`,
  boxShadow: '0 16px 45px rgba(1, 4, 73, 0.06)',
};

const primaryButtonSx = {
  bgcolor: BRAND.navy,
  borderRadius: 2.5,
  px: 3,
  py: 1.15,
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': { bgcolor: BRAND.blue },
};

const TabPanel = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
  </Box>
);

const SectionHeading = ({ icon, title, description }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 3 }}>
    <Box
      sx={{
        width: 42,
        height: 42,
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        color: BRAND.navy,
        bgcolor: BRAND.paleBlue,
        borderRadius: 2.5,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="h6" fontWeight={800} color={BRAND.navy}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
          {description}
        </Typography>
      )}
    </Box>
  </Stack>
);

const LocationMarker = ({ position, onChange }) => {
  useMapEvents({
    click(event) {
      onChange([event.latlng.lat, event.latlng.lng]);
    },
  });
  return position ? <Marker position={position} /> : null;
};

const TagManager = ({
  title,
  description,
  items,
  options,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  busy,
  icon,
}) => (
  <Card sx={{ ...sectionCardSx, height: '100%' }}>
    <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
      <SectionHeading icon={icon} title={title} description={description} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
        <Autocomplete
          freeSolo
          fullWidth
          options={options}
          inputValue={draft}
          onInputChange={(_, value) => onDraftChange(value)}
          onChange={(_, value) => onDraftChange(value || '')}
          renderInput={(params) => (
            <TextField {...params} label="Ajouter un élément" size="small" />
          )}
        />
        <Button
          variant="contained"
          disabled={busy || !draft.trim()}
          onClick={onAdd}
          startIcon={busy ? <CircularProgress size={17} color="inherit" /> : <AddRoundedIcon />}
          sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
        >
          Ajouter
        </Button>
      </Stack>

      <Divider sx={{ my: 2.5 }} />
      <Stack direction="row" gap={1} flexWrap="wrap">
        {items.map((item) => (
          <Chip
            key={item}
            label={translateLegacyLabel(item)}
            onDelete={busy ? undefined : () => onRemove(item)}
            sx={{
              bgcolor: BRAND.paleBlue,
              color: BRAND.navy,
              fontWeight: 650,
              '& .MuiChip-deleteIcon': { color: 'rgba(1,4,73,.55)' },
            }}
          />
        ))}
        {!items.length && (
          <Typography variant="body2" color="text.secondary">
            Aucun élément ajouté pour le moment.
          </Typography>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const Profile = () => {
  const { authTokens } = useContext(AuthContext);
  const {
    profile,
    setProfile,
    loading,
    error: profileError,
    refreshProfile,
  } = useContext(ProfileContext);

  const accessToken = authTokens?.access;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(null);
  const [hoursForm, setHoursForm] = useState(null);
  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [gallery, setGallery] = useState([]);
  const [features, setFeatures] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [featureDraft, setFeatureDraft] = useState('');
  const [equipmentDraft, setEquipmentDraft] = useState('');
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [galleryToDelete, setGalleryToDelete] = useState(null);
  const [cropSource, setCropSource] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (!profile) return;
    const structure = profile.structure || {};
    setForm({
      name: profile.name || '',
      owner_firstname: profile.owner_firstname || '',
      owner_lastname: profile.owner_lastname || '',
      description: profile.description || '',
      address: profile.address || '',
      structure: {
        area: numericValue(structure.area),
        height: numericValue(structure.height),
        floor: numericValue(structure.floor),
        parking: numericValue(structure.parking),
        elevator: numericValue(structure.elevator),
      },
      floor_materials: profile.floor_materials || [],
    });
    setPosition([
      Number(profile.lat) || DEFAULT_POSITION[0],
      Number(profile.lon) || DEFAULT_POSITION[1],
    ]);
    setProfileImageUrl(profileMediaUrl(profile.profile));
    setGallery(profile.gallery || []);
    setFeatures(profile.features || []);
    setEquipment(profile.equipment || []);
    const menHours = normalizeOpeningHours(profile.working_hours_men);
    const womenHours = normalizeOpeningHours(profile.working_hours_women);
    setHoursForm({
      sameForAll: profile.sex !== 'both'
        || JSON.stringify(menHours) === JSON.stringify(womenHours),
      men: menHours,
      women: womenHours,
    });
    setFieldErrors({});
  }, [profile]);

  const completion = useMemo(() => {
    if (!profile) return 0;
    const checks = [
      profile.name,
      profile.owner_firstname,
      profile.owner_lastname,
      profile.description,
      profile.address,
      profile.profile,
      (profile.features || []).length,
      (profile.equipment || []).length,
      (profile.gallery || []).length,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [profile]);

  const floorSelection = useMemo(
    () => (form?.floor_materials || []).map(
      (value) => FLOOR_OPTIONS.find((option) => option.value === value)
        || { value, label: value },
    ),
    [form?.floor_materials],
  );

  const notify = (severity, text) => setMessage({ severity, text });

  const handleAvailabilityChange = useCallback((active) => {
    setProfile((current) => ({ ...current, active }));
  }, [setProfile]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const updateStructure = (field, value) => {
    setForm((current) => ({
      ...current,
      structure: { ...current.structure, [field]: value },
    }));
    setFieldErrors((current) => {
      if (!current.structure) return current;
      const next = { ...current };
      delete next.structure;
      return next;
    });
  };

  const syncProfile = async () => {
    try {
      await refreshProfile();
    } catch (_) {
      // La sauvegarde est terminée : le rafraîchissement ne doit pas masquer son résultat.
    }
  };

  const saveInformation = async () => {
    const requiredErrors = {
      ...(!form.name.trim() ? { name: 'Le nom de la salle est obligatoire.' } : {}),
      ...(!form.owner_firstname.trim() ? { owner_firstname: 'Le prénom du responsable est obligatoire.' } : {}),
      ...(!form.owner_lastname.trim() ? { owner_lastname: 'Le nom du responsable est obligatoire.' } : {}),
      ...(!form.description.trim() ? { description: 'Présentez brièvement votre salle.' } : {}),
    };
    if (Object.keys(requiredErrors).length) {
      setFieldErrors(requiredErrors);
      notify('error', 'Complétez les champs signalés en rouge avant d’enregistrer.');
      return;
    }

    setFieldErrors({});
    setBusy('information');
    try {
      await updateGymProfile({
        name: form.name.trim(),
        owner_firstname: form.owner_firstname.trim(),
        owner_lastname: form.owner_lastname.trim(),
        description: form.description.trim(),
        structure: normalizeStructure(form.structure),
        floor_materials: form.floor_materials,
      }, accessToken);
      await syncProfile();
      notify('success', 'Les informations de la salle ont été enregistrées.');
    } catch (requestError) {
      setFieldErrors(requestError.fieldErrors || {});
      notify('error', requestError.message);
    } finally {
      setBusy('');
    }
  };

  const saveLocation = async () => {
    if (!form.address.trim()) {
      setFieldErrors((current) => ({ ...current, address: "L'adresse de la salle est obligatoire." }));
      notify('error', "L'adresse de la salle est obligatoire.");
      return;
    }

    setBusy('location');
    try {
      await updateGymProfile({
        address: form.address.trim(),
        lat: Number(position[0]),
        lon: Number(position[1]),
      }, accessToken);
      await syncProfile();
      notify('success', 'L’adresse et la position ont été mises à jour.');
    } catch (requestError) {
      setFieldErrors(requestError.fieldErrors || {});
      notify('error', requestError.message);
    } finally {
      setBusy('');
    }
  };

  const saveOpeningHours = async () => {
    if (!hoursForm) return;

    const menHours = hoursForm.men;
    const womenHours = hoursForm.sameForAll ? hoursForm.men : hoursForm.women;
    setBusy('hours');
    try {
      await updateGymProfile({
        working_hours_men: menHours,
        working_hours_women: womenHours,
      }, accessToken);
      setHoursForm((current) => ({
        ...current,
        men: menHours,
        women: womenHours,
      }));
      setProfile((current) => ({
        ...current,
        working_hours_men: menHours,
        working_hours_women: womenHours,
      }));
      notify('success', 'Les horaires d’ouverture ont été enregistrés.');
    } catch (requestError) {
      notify('error', requestError.message || "Les horaires n'ont pas pu être enregistrés.");
    } finally {
      setBusy('');
    }
  };

  const addListItem = async (kind) => {
    const isFeature = kind === 'feature';
    const value = (isFeature ? featureDraft : equipmentDraft).trim();
    if (!value) return;

    setBusy(kind);
    try {
      const data = isFeature
        ? await addGymFeature(value, accessToken)
        : await addGymEquipment(value, accessToken);
      if (isFeature) {
        const next = data.features || [...features, value];
        setFeatures(next);
        setFeatureDraft('');
        setProfile((current) => ({ ...current, features: next }));
      } else {
        const next = data.equipment || [...equipment, value];
        setEquipment(next);
        setEquipmentDraft('');
        setProfile((current) => ({ ...current, equipment: next }));
      }
      notify('success', isFeature ? 'Le service a été ajouté.' : "L'équipement a été ajouté.");
    } catch (requestError) {
      notify('error', requestError.message);
    } finally {
      setBusy('');
    }
  };

  const removeListItem = async (kind, value) => {
    const isFeature = kind === 'feature';
    setBusy(kind);
    try {
      const data = isFeature
        ? await removeGymFeature(value, accessToken)
        : await removeGymEquipment(value, accessToken);
      if (isFeature) {
        const next = data.features || features.filter((item) => item !== value);
        setFeatures(next);
        setProfile((current) => ({ ...current, features: next }));
      } else {
        const next = data.equipment || equipment.filter((item) => item !== value);
        setEquipment(next);
        setProfile((current) => ({ ...current, equipment: next }));
      }
      notify('success', isFeature ? 'Le service a été supprimé.' : "L'équipement a été supprimé.");
    } catch (requestError) {
      notify('error', requestError.message);
    } finally {
      setBusy('');
    }
  };

  const handleProfileImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notify('error', 'Sélectionnez un fichier image valide.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      notify('error', 'La photo ne doit pas dépasser 8 Mo.');
      return;
    }
    setCropSource(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const closeCropDialog = () => {
    if (cropSource) URL.revokeObjectURL(cropSource);
    setCropSource('');
    setCroppedAreaPixels(null);
  };

  const handleCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const uploadCroppedProfile = async () => {
    if (!cropSource || !croppedAreaPixels) return;
    setBusy('profile-image');
    try {
      const blob = await getCroppedImg(cropSource, croppedAreaPixels);
      const data = new FormData();
      data.append('image', blob, 'profil-salle.jpg');
      const result = await updateProfileImage(data, accessToken);
      const imageName = result.profile_image || profile.profile;
      setProfileImageUrl(`${profileMediaUrl(imageName)}?v=${Date.now()}`);
      setProfile((current) => ({ ...current, profile: imageName }));
      notify('success', 'La photo de profil a été mise à jour.');
      closeCropDialog();
    } catch (_) {
      notify('error', "La photo n'a pas pu être envoyée.");
    } finally {
      setBusy('');
    }
  };

  const uploadGalleryImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (gallery.length >= 10) {
      notify('warning', 'La galerie peut contenir au maximum 10 photos.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      notify('error', 'Sélectionnez un fichier image valide.');
      return;
    }

    setBusy('gallery');
    try {
      const data = new FormData();
      data.append('image', file);
      const result = await addGalleryImage(data, accessToken);
      const next = result.gallery || gallery;
      setGallery(next);
      setProfile((current) => ({ ...current, gallery: next }));
      notify('success', 'La photo a été ajoutée à la galerie.');
    } catch (_) {
      notify('error', "La photo n'a pas pu être ajoutée.");
    } finally {
      setBusy('');
    }
  };

  const confirmGalleryDelete = async () => {
    if (!galleryToDelete) return;
    setBusy('gallery-delete');
    try {
      const result = await removeGalleryImage(galleryToDelete, accessToken);
      const next = result.gallery || gallery.filter((item) => item !== galleryToDelete);
      setGallery(next);
      setProfile((current) => ({ ...current, gallery: next }));
      setGalleryToDelete(null);
      notify('success', 'La photo a été supprimée.');
    } catch (_) {
      notify('error', "La photo n'a pas pu être supprimée.");
    } finally {
      setBusy('');
    }
  };

  const resendVerification = async () => {
    setBusy('verification');
    try {
      const result = await sendEmailVerification(profile.owner_email);
      if (result?.remaining_time) {
        notify('info', `Un e-mail a déjà été envoyé. Réessayez dans ${Math.ceil(result.remaining_time)} secondes.`);
      } else {
        notify('success', "L'e-mail de vérification a été envoyé.");
      }
    } catch (_) {
      notify('error', "L'e-mail de vérification n'a pas pu être envoyé.");
    } finally {
      setBusy('');
    }
  };

  if (loading && !profile) {
    return (
      <Box sx={{ maxWidth: 1180, mx: 'auto', py: 8 }}>
        <LinearProgress sx={{ borderRadius: 4 }} />
        <Typography align="center" color="text.secondary" sx={{ mt: 2 }}>
          Chargement du profil de votre salle…
        </Typography>
      </Box>
    );
  }

  if (!profile || !form || !hoursForm) {
    return (
      <Alert severity="error" sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
        {profileError || "Le profil de la salle n'a pas pu être chargé."}
      </Alert>
    );
  }

  const emailVerified = Boolean(profile.email_verified);
  const commercialActive = Boolean(profile.active);

  return (
    <Box
      dir="ltr"
      sx={{
        mx: { xs: -2, sm: 0 },
        px: { xs: 1.5, sm: 0 },
        py: { xs: 1, md: 2 },
        color: '#172033',
        fontFamily: 'Inter, Roboto, Arial, sans-serif',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Card
          sx={{
            mb: 3,
            overflow: 'visible',
            borderRadius: 5,
            color: 'white',
            background: `linear-gradient(125deg, ${BRAND.navy} 0%, #121A72 58%, ${BRAND.blue} 100%)`,
            boxShadow: '0 22px 55px rgba(1, 4, 73, 0.25)',
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={3}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={profileImageUrl}
                    alt={`Photo de ${profile.name}`}
                    imgProps={{
                      onError: (event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_GYM_PROFILE;
                      },
                    }}
                    sx={{
                      width: { xs: 104, md: 124 },
                      height: { xs: 104, md: 124 },
                      border: '4px solid rgba(255,255,255,.9)',
                      bgcolor: 'rgba(255,255,255,.15)',
                      fontSize: 42,
                      fontWeight: 800,
                    }}
                  >
                    {(profile.name || 'S').charAt(0).toUpperCase()}
                  </Avatar>
                  <Tooltip title="Modifier la photo">
                    <IconButton
                      component="label"
                      sx={{
                        position: 'absolute', right: -4, bottom: 4,
                        bgcolor: BRAND.orange, color: 'white', boxShadow: 3,
                        '&:hover': { bgcolor: '#DA781E' },
                      }}
                    >
                      <PhotoCameraRoundedIcon fontSize="small" />
                      <input hidden type="file" accept="image/*" onChange={handleProfileImage} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box>
                  <Typography variant="overline" sx={{ opacity: 0.72, letterSpacing: 1.4 }}>
                    PROFIL DE LA SALLE
                  </Typography>
                  <Typography variant="h3" fontWeight={900} sx={{ fontSize: { xs: 30, md: 42 }, lineHeight: 1.15 }}>
                    {profile.name}
                  </Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                    <Chip
                      size="small"
                      icon={<StorefrontRoundedIcon />}
                      label={commercialActive ? 'Souscriptions ouvertes' : 'Souscriptions suspendues'}
                      sx={{ bgcolor: 'rgba(255,255,255,.14)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                    />
                    <Chip
                      size="small"
                      icon={emailVerified ? <VerifiedRoundedIcon /> : <MailOutlineRoundedIcon />}
                      label={emailVerified ? 'E-mail vérifié' : 'E-mail à vérifier'}
                      sx={{ bgcolor: 'rgba(255,255,255,.14)', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                    />
                  </Stack>
                  {profile.address && (
                    <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 1.5, opacity: 0.78 }}>
                      <PlaceRoundedIcon fontSize="small" />
                      <Typography variant="body2">{profile.address}</Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>

              <Box
                sx={{
                  minWidth: { md: 190 }, p: 2, borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,.10)',
                  border: '1px solid rgba(255,255,255,.15)',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ opacity: 0.78 }}>Profil complété</Typography>
                  <Typography variant="h6" fontWeight={900}>{completion}%</Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completion}
                  sx={{
                    mt: 1.2, height: 7, borderRadius: 8,
                    bgcolor: 'rgba(255,255,255,.15)',
                    '& .MuiLinearProgress-bar': { bgcolor: BRAND.orange, borderRadius: 8 },
                  }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <GymAvailabilityCard
          accessToken={accessToken}
          initialActive={profile.active}
          onAvailabilityChange={handleAvailabilityChange}
        />

        {!emailVerified && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 3 }}
            action={(
              <Button color="inherit" size="small" disabled={busy === 'verification'} onClick={resendVerification}>
                {busy === 'verification' ? 'Envoi…' : "Renvoyer l'e-mail"}
              </Button>
            )}
          >
            Vérifiez l’adresse e-mail du propriétaire pour sécuriser le compte de la salle.
          </Alert>
        )}

        <Card sx={{ ...sectionCardSx, overflow: 'hidden' }}>
          <Tabs
            value={tab}
            onChange={(_, nextTab) => setTab(nextTab)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              px: { xs: 1, md: 2 }, borderBottom: `1px solid ${BRAND.border}`,
              '& .MuiTab-root': { minHeight: 68, textTransform: 'none', fontWeight: 750 },
              '& .Mui-selected': { color: `${BRAND.navy} !important` },
              '& .MuiTabs-indicator': { bgcolor: BRAND.orange, height: 3 },
            }}
          >
            <Tab icon={<InfoRoundedIcon />} iconPosition="start" label="Informations" />
            <Tab icon={<AccessTimeRoundedIcon />} iconPosition="start" label="Horaires" />
            <Tab icon={<FitnessCenterRoundedIcon />} iconPosition="start" label="Services et équipements" />
            <Tab icon={<CollectionsRoundedIcon />} iconPosition="start" label="Photos" />
            <Tab icon={<PlaceRoundedIcon />} iconPosition="start" label="Localisation" />
          </Tabs>

          <TabPanel value={tab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={8}>
                <SectionHeading
                  icon={<StorefrontRoundedIcon />}
                  title="Informations publiques"
                  description="Ces informations sont visibles par les clients sur la fiche de votre salle."
                />
                {Object.keys(fieldErrors).length > 0 && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>
                    Certains champs doivent être corrigés. Ils sont indiqués en rouge ci-dessous.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth required label="Nom de la salle" value={form.name}
                      onChange={(event) => updateForm('name', event.target.value)}
                      error={Boolean(fieldErrors.name)}
                      helperText={fieldErrors.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Code de la salle" value={profile.gym_code || 'Non attribué'} disabled />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth required label="Prénom du responsable" value={form.owner_firstname}
                      onChange={(event) => updateForm('owner_firstname', event.target.value)}
                      error={Boolean(fieldErrors.owner_firstname)}
                      helperText={fieldErrors.owner_firstname}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth required label="Nom du responsable" value={form.owner_lastname}
                      onChange={(event) => updateForm('owner_lastname', event.target.value)}
                      error={Boolean(fieldErrors.owner_lastname)}
                      helperText={fieldErrors.owner_lastname}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth required multiline minRows={4} label="Présentation de la salle"
                      placeholder="Présentez l’ambiance, les activités et les points forts de votre salle."
                      value={form.description}
                      onChange={(event) => updateForm('description', event.target.value)}
                      inputProps={{ maxLength: 900 }}
                      error={Boolean(fieldErrors.description)}
                      helperText={fieldErrors.description || `${form.description.length}/900 caractères`}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', bgcolor: '#FAFBFE', borderColor: BRAND.border }}>
                  <CardContent>
                    <Typography fontWeight={800} color={BRAND.navy} gutterBottom>Coordonnées du compte</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <MailOutlineRoundedIcon sx={{ color: BRAND.blue }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary">Adresse e-mail</Typography>
                          <Typography variant="body2" fontWeight={650} noWrap>{profile.owner_email || 'Non renseignée'}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <PhoneRoundedIcon sx={{ color: BRAND.blue }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                          <Typography variant="body2" fontWeight={650}>{profile.phone_number || 'Non renseigné'}</Typography>
                        </Box>
                      </Stack>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <PersonRoundedIcon sx={{ color: BRAND.blue }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Public accueilli</Typography>
                          <Typography variant="body2" fontWeight={650}>
                            {profile.sex === 'men' ? 'Hommes' : profile.sex === 'women' ? 'Femmes' : 'Tous publics'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <SectionHeading
                  icon={<ApartmentRoundedIcon />}
                  title="Caractéristiques du bâtiment"
                  description="Ajoutez les informations qui aident les clients à préparer leur visite."
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth type="number" label="Surface (m²)" value={form.structure.area}
                      onChange={(event) => updateStructure('area', event.target.value)}
                      InputProps={{ startAdornment: <SquareFootRoundedIcon sx={{ mr: 1, color: 'text.disabled' }} /> }}
                      error={Boolean(fieldErrors.structure)}
                      helperText={fieldErrors.structure}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth type="number" label="Hauteur sous plafond (m)" value={form.structure.height} onChange={(event) => updateStructure('height', event.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField fullWidth type="number" label="Étages" value={form.structure.floor} onChange={(event) => updateStructure('floor', event.target.value)} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select fullWidth label="Parking" value={form.structure.parking}
                      onChange={(event) => updateStructure('parking', event.target.value)}
                      InputProps={{ startAdornment: <LocalParkingRoundedIcon sx={{ mr: 1, color: 'text.disabled' }} /> }}
                    >
                      <MenuItem value="">Non renseigné</MenuItem>
                      <MenuItem value={1}>Disponible</MenuItem>
                      <MenuItem value={0}>Non disponible</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      select fullWidth label="Ascenseur" value={form.structure.elevator}
                      onChange={(event) => updateStructure('elevator', event.target.value)}
                      InputProps={{ startAdornment: <ElevatorRoundedIcon sx={{ mr: 1, color: 'text.disabled' }} /> }}
                    >
                      <MenuItem value="">Non renseigné</MenuItem>
                      <MenuItem value={1}>Disponible</MenuItem>
                      <MenuItem value={0}>Non disponible</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple limitTags={5} options={FLOOR_OPTIONS}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      value={floorSelection}
                      onChange={(_, values) => updateForm('floor_materials', values.slice(0, 5).map((item) => item.value))}
                      renderInput={(params) => (
                        <TextField {...params} label="Revêtements de sol" helperText="Vous pouvez sélectionner jusqu’à cinq revêtements." />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="contained" disabled={busy === 'information'} onClick={saveInformation}
                startIcon={busy === 'information' ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                sx={primaryButtonSx}
              >
                Enregistrer les informations
              </Button>
            </Stack>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <OpeningHoursEditor
              value={hoursForm}
              onChange={setHoursForm}
              gymSex={profile.sex}
              busy={busy === 'hours'}
              onSave={saveOpeningHours}
            />
          </TabPanel>

          <TabPanel value={tab} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={6}>
                <TagManager
                  title="Services proposés"
                  description="Les services et espaces accessibles dans votre établissement."
                  items={features} options={FEATURE_OPTIONS} draft={featureDraft}
                  onDraftChange={setFeatureDraft} onAdd={() => addListItem('feature')}
                  onRemove={(value) => removeListItem('feature', value)}
                  busy={busy === 'feature'} icon={<StorefrontRoundedIcon />}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <TagManager
                  title="Équipements sportifs"
                  description="Les machines et zones d’entraînement disponibles."
                  items={equipment} options={EQUIPMENT_OPTIONS} draft={equipmentDraft}
                  onDraftChange={setEquipmentDraft} onAdd={() => addListItem('equipment')}
                  onRemove={(value) => removeListItem('equipment', value)}
                  busy={busy === 'equipment'} icon={<FitnessCenterRoundedIcon />}
                />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <SectionHeading
              icon={<CollectionsRoundedIcon />}
              title="Galerie de la salle"
              description="Ajoutez des photos lumineuses des espaces, équipements et vestiaires. Dix photos maximum."
            />

            <Grid container spacing={2}>
              {!gallery.length && (
                <Grid item xs={12} sm={6} md={4}>
                  <Box
                    sx={{
                      position: 'relative', height: 220, overflow: 'hidden', borderRadius: 3,
                      bgcolor: '#E9EDF5', border: `1px solid ${BRAND.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={DEFAULT_GYM_GALLERY}
                      alt="Vue par défaut de la salle"
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Chip
                      label="Image par défaut"
                      size="small"
                      sx={{ position: 'absolute', left: 10, bottom: 10, bgcolor: 'rgba(255,255,255,.92)' }}
                    />
                  </Box>
                </Grid>
              )}
              {gallery.map((image) => (
                <Grid item xs={12} sm={6} md={4} key={image}>
                  <Box
                    sx={{
                      position: 'relative', height: 220, overflow: 'hidden', borderRadius: 3,
                      bgcolor: '#E9EDF5', border: `1px solid ${BRAND.border}`,
                      '&:hover img': { transform: 'scale(1.04)' },
                    }}
                  >
                    <Box
                      component="img" src={galleryMediaUrl(image)} alt="Vue de la salle"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_GYM_GALLERY;
                      }}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform .3s ease' }}
                    />
                    <Tooltip title="Supprimer cette photo">
                      <IconButton
                        onClick={() => setGalleryToDelete(image)}
                        sx={{
                          position: 'absolute', top: 10, right: 10,
                          bgcolor: 'rgba(255,255,255,.92)', color: 'error.main',
                          '&:hover': { bgcolor: 'white' },
                        }}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              ))}

              {gallery.length < 10 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    component="label" disabled={busy === 'gallery'}
                    sx={{
                      width: '100%', height: 220, display: 'flex', flexDirection: 'column', gap: 1,
                      border: `2px dashed ${BRAND.blue}`, borderRadius: 3,
                      bgcolor: BRAND.paleBlue, color: BRAND.navy, textTransform: 'none',
                      '&:hover': { bgcolor: '#E3EAFF', borderStyle: 'solid' },
                    }}
                  >
                    {busy === 'gallery' ? <CircularProgress size={30} /> : <CloudUploadRoundedIcon sx={{ fontSize: 38 }} />}
                    <Typography fontWeight={800}>Ajouter une photo</Typography>
                    <Typography variant="caption" color="text.secondary">JPG ou PNG</Typography>
                    <input hidden type="file" accept="image/*" onChange={uploadGalleryImage} />
                  </Button>
                </Grid>
              )}
            </Grid>

            {!gallery.length && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2.5 }}>
                Une image Gyma est affichée par défaut. Ajoutez vos propres photos pour personnaliser votre fiche.
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tab} index={4}>
            <SectionHeading
              icon={<PlaceRoundedIcon />}
              title="Adresse et position sur la carte"
              description="Cliquez sur la carte pour placer précisément l’entrée de votre salle."
            />

            <TextField
              fullWidth required label="Adresse complète" placeholder="Numéro, voie, code postal et ville"
              value={form.address} onChange={(event) => updateForm('address', event.target.value)} sx={{ mb: 2 }}
              error={Boolean(fieldErrors.address)}
              helperText={fieldErrors.address}
            />

            <Box
              sx={{
                height: { xs: 340, md: 460 }, overflow: 'hidden', borderRadius: 4,
                border: `1px solid ${BRAND.border}`,
                '& .leaflet-container': { width: '100%', height: '100%' },
              }}
            >
              <MapContainer center={position} zoom={14} scrollWheelZoom>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
                <LocationMarker position={position} onChange={setPosition} />
              </MapContainer>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
              justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}
            >
              <Chip
                icon={<MyLocationRoundedIcon />}
                label={`${Number(position[0]).toFixed(5)}, ${Number(position[1]).toFixed(5)}`}
                sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
              />
              <Button
                variant="contained" disabled={busy === 'location'} onClick={saveLocation}
                startIcon={busy === 'location' ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
                sx={primaryButtonSx}
              >
                Enregistrer la localisation
              </Button>
            </Stack>
          </TabPanel>
        </Card>
      </Box>

      <Dialog open={Boolean(cropSource)} onClose={closeCropDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: BRAND.navy }}>Recadrer la photo de profil</DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', height: 360, bgcolor: '#11152E', borderRadius: 3, overflow: 'hidden' }}>
            {cropSource && (
              <Cropper
                image={cropSource} crop={crop} zoom={zoom} aspect={1}
                cropShape="round" showGrid={false} onCropChange={setCrop}
                onZoomChange={setZoom} onCropComplete={handleCropComplete}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeCropDialog} disabled={busy === 'profile-image'}>Annuler</Button>
          <Button
            variant="contained" onClick={uploadCroppedProfile} disabled={busy === 'profile-image'}
            startIcon={busy === 'profile-image' ? <CircularProgress size={17} color="inherit" /> : <PhotoCameraRoundedIcon />}
            sx={primaryButtonSx}
          >
            Enregistrer la photo
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(galleryToDelete)} onClose={() => setGalleryToDelete(null)}>
        <DialogTitle sx={{ fontWeight: 800 }}>Supprimer cette photo ?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">La photo sera définitivement retirée de la galerie de votre salle.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGalleryToDelete(null)} disabled={busy === 'gallery-delete'}>Annuler</Button>
          <Button color="error" variant="contained" onClick={confirmGalleryDelete} disabled={busy === 'gallery-delete'}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)} autoHideDuration={5500} onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={message?.severity || 'info'} onClose={() => setMessage(null)} sx={{ width: '100%' }}>
          {message?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Profile;
